import type {
  FormColumn as ServiceFormColumn,
} from "../../services/calculatorService";

/**
 * Procurement (Wet Lab Silver) — the client-side half, issue #305.
 *
 * The frontend holds **no consumable data**. Which items exist, what they weigh,
 * what they cost and what they emit all live server-side; the client only knows
 * how to render whatever the schema hands it. What lives here is display copy
 * plus the generic option-resolution helpers.
 *
 * The assessment is a single add-row table. Each row names one consumable via
 * three cascading dropdowns (Item Category, then Item Type, then Size / Volume),
 * then captures order quantities and a reuse option. Five completed rows are
 * required to submit and no item may appear twice.
 */

export const PROCUREMENT_CATEGORY = "Procurement";

/** Five completed rows before submit unlocks. Any mix of categories: Alex,
 * "one row counts as one item, no matter what category that item's in". */
export const MIN_PROCUREMENT_ITEMS = 5;

/** Column labels, in display order. Matched by label rather than id because the
 * backend assigns ids and the client must not assume their values. */
export const PROCUREMENT_COLUMNS = {
  itemCategory: "Item Category",
  itemType: "Item Type",
  sizeVolume: "Size / Volume",
  catalogueNumber: "Catalogue Number",
  unitsPerPack: "Units per Pack",
  packsPerOrder: "Packs per Order",
  monthsBetweenOrders: "Months Between Orders",
  reuseOrAlternatives: "Reuse or Alternatives?",
} as const;

/** The three columns that together identify which consumable a row is about.
 * Ordered parent-first: each clears the ones after it when changed, and
 * together they form the uniqueness key. */
export const PROCUREMENT_IDENTITY_COLUMNS: string[] = [
  PROCUREMENT_COLUMNS.itemCategory,
  PROCUREMENT_COLUMNS.itemType,
  PROCUREMENT_COLUMNS.sizeVolume,
];

/**
 * Compact header text for the wide table only.
 *
 * The app caps assessment content at ~1104px (`.l0-layout`), so a column can be
 * under 90px. Rather than shrink type below the legible floor or let words break
 * mid-character, the header is shortened and the full wording is carried by the
 * tooltip and the aria-label. The stacked layout has room and keeps full labels.
 */
export const PROCUREMENT_HEADER_LABELS: Record<string, string> = {
  [PROCUREMENT_COLUMNS.itemCategory]: "Category",
  [PROCUREMENT_COLUMNS.itemType]: "Item Type",
  [PROCUREMENT_COLUMNS.sizeVolume]: "Size",
  [PROCUREMENT_COLUMNS.catalogueNumber]: "Cat. No.",
  [PROCUREMENT_COLUMNS.unitsPerPack]: "Units/pack",
  [PROCUREMENT_COLUMNS.packsPerOrder]: "Packs/order",
  [PROCUREMENT_COLUMNS.monthsBetweenOrders]: "Months",
  [PROCUREMENT_COLUMNS.reuseOrAlternatives]: "Reuse?",
};

/**
 * Fallback help text, used when the backend sends a column with no
 * `description`. The issue supplies wording for the first three; the rest are
 * written in the same plain register and are worth confirming with the author.
 */
export const PROCUREMENT_TOOLTIPS: Record<string, string> = {
  [PROCUREMENT_COLUMNS.itemCategory]: "The general group this item belongs to.",
  [PROCUREMENT_COLUMNS.itemType]: "The specific item used in the lab.",
  [PROCUREMENT_COLUMNS.sizeVolume]:
    "The size or volume of the item you usually use.",
  [PROCUREMENT_COLUMNS.catalogueNumber]:
    "Optional supplier or catalogue reference for this item.",
  [PROCUREMENT_COLUMNS.unitsPerPack]: "How many individual items come in one pack.",
  [PROCUREMENT_COLUMNS.packsPerOrder]: "How many packs you order at a time.",
  [PROCUREMENT_COLUMNS.monthsBetweenOrders]:
    "How many months typically pass between orders. Estimates are fine.",
  [PROCUREMENT_COLUMNS.reuseOrAlternatives]:
    "Whether this item is reused, or whether a lower-impact material is used instead.",
};

/** `{ columnId: value }` for one row, which is what the conditions read. */
type RowValues = Record<string, string>;

/**
 * The options a column offers given the rest of its row.
 *
 * Returns `null` when the column has conditions but its parent is unset — the
 * caller renders a disabled control rather than an empty dropdown, so "pick the
 * category first" is visible rather than inferred from an empty list.
 *
 * Columns with no conditions return their plain `allowed_values`, which is what
 * keeps every other assessment working unchanged.
 */
export const resolveAllowedValues = (
  column: ServiceFormColumn,
  rowValues: RowValues,
): string[] | null => {
  const conditions = column.constraints.conditional_allowed_values;
  if (!conditions || conditions.length === 0) {
    return column.constraints.allowed_values ?? [];
  }

  const parentId = String(conditions[0]?.depends_on_column_id ?? "");
  const parentValue = (rowValues[parentId] ?? "").trim();
  if (!parentValue) return null;

  const match = conditions.find(
    (condition) =>
      String(condition.depends_on_column_id) === parentId &&
      condition.when_matches === parentValue,
  );
  return match ? match.allowed_values : (column.constraints.allowed_values ?? []);
};

/** The column a conditional column depends on, or null if it is independent. */
export const parentColumnIdOf = (column: ServiceFormColumn): string | null => {
  const first = column.constraints.conditional_allowed_values?.[0];
  return first ? String(first.depends_on_column_id) : null;
};

/**
 * The columns directly below `changedColumnId`, in the order they appear.
 *
 * Without this a row keeps an item combination that no longer exists — pick Cell
 * Culture / Flask / T25 then switch to Dishes, and the row still claims Flask
 * T25. The server would reject it at submit, long after the user moved on.
 *
 * Direct children only, deliberately. Clearing one runs the same clear again for
 * that column, so the whole chain still empties; returning the transitive set
 * here meant every grandchild was cleared twice, the second time against a stale
 * read, which could fire a duplicate save. Staying one level deep also means
 * this cannot recurse, so a malformed schema with a cyclic dependency can no
 * longer overflow the stack.
 */
export const dependentColumnIdsOf = (
  columns: ServiceFormColumn[],
  changedColumnId: string,
): string[] =>
  columns
    .filter((column) => parentColumnIdOf(column) === changedColumnId)
    .map((column) => String(column.id));

/**
 * Joins the identity triple into one key. An ASCII unit separator, written as an
 * escape rather than the raw byte: the literal character is invisible in most
 * editors and silently corruptible in a diff. Chosen because it cannot appear in
 * an item name, so no combination of values can collide.
 */
const ITEM_KEY_SEPARATOR = "\u001F";

/**
 * The identity triple of a row, as a comparable key.
 *
 * Maz: "they can't add Microcentrifuge tube 1.5ml 5 times". Alex confirmed the
 * whole combo is the key, so Microcentrifuge Tube 2 mL is still a valid separate
 * row. Returns null while the triple is incomplete, since a half-filled row
 * cannot yet duplicate anything.
 */
export const procurementItemKey = (
  columns: ServiceFormColumn[],
  rowValues: RowValues,
): string | null => {
  const parts = PROCUREMENT_IDENTITY_COLUMNS.map((label) => {
    const column = columns.find((c) => c.label === label);
    return column ? (rowValues[String(column.id)] ?? "").trim() : "";
  });
  return parts.every(Boolean) ? parts.join(ITEM_KEY_SEPARATOR) : null;
};

/**
 * Options for `column` in `rowId`, minus anything that would create a duplicate
 * item in another row.
 *
 * Filtering rather than only validating means a duplicate can never be picked in
 * the first place. Each level also hides parents whose children are exhausted, so
 * a row can't be walked into a dead end — pick a category, then find every one of
 * its item types already taken.
 */
export const availableProcurementOptions = (
  column: ServiceFormColumn,
  columns: ServiceFormColumn[],
  rowId: string,
  rowValues: RowValues,
  allRows: Record<string, RowValues>,
): string[] | null => {
  const options = resolveAllowedValues(column, rowValues);
  if (options === null) return null;

  const identityIndex = PROCUREMENT_IDENTITY_COLUMNS.indexOf(column.label);
  if (identityIndex === -1) return options;

  const takenKeys = new Set(
    Object.entries(allRows)
      .filter(([otherRowId]) => otherRowId !== rowId)
      .map(([, values]) => procurementItemKey(columns, values))
      .filter((key): key is string => key !== null),
  );
  if (takenKeys.size === 0) return options;

  // An option survives if at least one complete item starting with it is still
  // free. Walking forward from the column being filtered means the same check
  // covers all three levels: a size, a type's sizes, or a category's types.
  const prefix = PROCUREMENT_IDENTITY_COLUMNS.slice(0, identityIndex).map(
    (label) => {
      const parent = columns.find((c) => c.label === label);
      return parent ? (rowValues[String(parent.id)] ?? "").trim() : "";
    },
  );

  return options.filter((option) =>
    hasFreeCompletion(columns, [...prefix, option], identityIndex + 1, takenKeys),
  );
};

/**
 * Whether any complete item exists with this prefix that isn't already taken.
 * Recurses down the remaining identity columns using their own conditions.
 */
const hasFreeCompletion = (
  columns: ServiceFormColumn[],
  prefix: string[],
  nextIndex: number,
  takenKeys: Set<string>,
): boolean => {
  if (nextIndex >= PROCUREMENT_IDENTITY_COLUMNS.length) {
    return !takenKeys.has(prefix.join(ITEM_KEY_SEPARATOR));
  }

  const nextLabel = PROCUREMENT_IDENTITY_COLUMNS[nextIndex];
  const nextColumn = columns.find((c) => c.label === nextLabel);
  if (!nextColumn) return true;

  const parentId = parentColumnIdOf(nextColumn);
  const parentValue = prefix[prefix.length - 1] ?? "";
  const conditions = nextColumn.constraints.conditional_allowed_values;

  const candidates =
    parentId && conditions
      ? (conditions.find((c) => c.when_matches === parentValue)?.allowed_values ??
        [])
      : (nextColumn.constraints.allowed_values ?? []);

  return candidates.some((candidate) =>
    hasFreeCompletion(columns, [...prefix, candidate], nextIndex + 1, takenKeys),
  );
};
